import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Mail, MessageSquare, Phone, MapPin, ArrowLeft } from 'lucide-react';

export default function ContactPage() {
  const email = 'info@scorecopilot.com';
  
  return (
    <div className="min-h-screen bg-[#0f0f10]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="/logo.jpg" 
                alt="Score Copilot" 
                className="h-12 w-auto object-contain"
              />
              <span className="font-heading text-2xl font-bold text-white uppercase tracking-wide">
                Score Copilot
              </span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/pricing" className="text-gray-400 hover:text-white transition-colors">
                Pricing
              </Link>
              <Link to="/login" className="text-gray-400 hover:text-white transition-colors">
                Sign In
              </Link>
              <Link to="/register">
                <Button className="bg-emerald-500 hover:bg-emerald-600 text-white">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Contact Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Back Link */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft size={18} />
            Back to Home
          </Link>

          <div className="text-center mb-12">
            <h1 className="font-heading text-5xl md:text-6xl font-bold text-white uppercase mb-4">
              Contact Us
            </h1>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Have questions about ScoreCopilot? Need help with your tournament? 
              We're here to help!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {/* Email Card - Primary CTA */}
            <Card className="bg-[#18181b] border-emerald-500/50 emerald-glow md:col-span-2">
              <CardContent className="p-8">
                <div className="flex flex-col md:flex-row items-center gap-6">
                  <div className="w-16 h-16 bg-emerald-500/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Mail className="text-emerald-500" size={32} />
                  </div>
                  <div className="flex-1 text-center md:text-left">
                    <h2 className="font-heading text-2xl font-bold text-white uppercase mb-2">
                      Email Us
                    </h2>
                    <p className="text-gray-400 mb-4">
                      Send us an email and we'll get back to you within 24 hours.
                    </p>
                    <a 
                      href={`mailto:${email}`}
                      className="text-emerald-500 text-xl font-medium hover:text-emerald-400 transition-colors"
                    >
                      {email}
                    </a>
                  </div>
                  <a href={`mailto:${email}`}>
                    <Button 
                      size="lg"
                      className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 btn-lift"
                      data-testid="contact-email-btn"
                    >
                      <Mail className="mr-2" size={20} />
                      Send Email
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>

            {/* Support Card */}
            <Card className="bg-[#18181b] border-white/10">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="text-blue-500" size={24} />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-bold text-white uppercase mb-2">
                      Support
                    </h3>
                    <p className="text-gray-400 text-sm mb-3">
                      Need help with your account or tournament setup?
                    </p>
                    <a 
                      href={`mailto:${email}?subject=Support Request`}
                      className="text-blue-500 hover:text-blue-400 text-sm font-medium"
                    >
                      Get Support →
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Sales Card */}
            <Card className="bg-[#18181b] border-white/10">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="text-purple-500" size={24} />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-bold text-white uppercase mb-2">
                      Sales
                    </h3>
                    <p className="text-gray-400 text-sm mb-3">
                      Interested in Enterprise plan or custom solutions?
                    </p>
                    <a 
                      href={`mailto:${email}?subject=Enterprise Inquiry`}
                      className="text-purple-500 hover:text-purple-400 text-sm font-medium"
                    >
                      Contact Sales →
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* FAQ Section */}
          <div className="bg-[#18181b] border border-white/10 rounded-xl p-8">
            <h2 className="font-heading text-2xl font-bold text-white uppercase mb-6 text-center">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              <div className="border-b border-white/10 pb-4">
                <h3 className="text-white font-medium mb-2">How quickly can I set up my tournament?</h3>
                <p className="text-gray-400 text-sm">
                  You can have your tournament up and running in under 5 minutes. We even provide sample data to get you started!
                </p>
              </div>
              <div className="border-b border-white/10 pb-4">
                <h3 className="text-white font-medium mb-2">Can I upgrade my plan later?</h3>
                <p className="text-gray-400 text-sm">
                  Yes! You can upgrade anytime from your billing page. Your data stays intact when you upgrade.
                </p>
              </div>
              <div className="border-b border-white/10 pb-4">
                <h3 className="text-white font-medium mb-2">Do you offer refunds?</h3>
                <p className="text-gray-400 text-sm">
                  Yes, we offer a 30-day money-back guarantee on all paid plans. No questions asked.
                </p>
              </div>
              <div>
                <h3 className="text-white font-medium mb-2">Is my data secure?</h3>
                <p className="text-gray-400 text-sm">
                  Absolutely. We use industry-standard encryption and never share your data with third parties.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center gap-4 mb-8">
            <img src="/cnvt-logo.jpg" alt="CNVT" className="w-10 h-10 rounded object-contain" />
            <span className="text-gray-600 text-sm">|</span>
            <img src="/kilimanjaro-logo.png" alt="Kilimanjaro" className="h-10 object-contain" />
            <span className="text-gray-600 text-sm">|</span>
            <img src="/africremit-logo.jpg" alt="Afric Remit" className="h-10 object-contain rounded" />
          </div>
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img 
                src="/logo.jpg" 
                alt="Score Copilot" 
                className="h-10 w-auto object-contain"
              />
              <span className="font-heading text-xl font-bold text-white uppercase">Score Copilot</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
              <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
            </div>
            <p className="text-gray-500 text-sm">
              © 2026 Sports Copilot. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
